import type { Reservation, ReservationStatus } from './hospitalityTypes';

/** Estados que todavía admiten acción de salón (confirmar / sentar / no-show). */
const ACTIONABLE_STATUSES: ReadonlySet<ReservationStatus> = new Set(['pending', 'confirmed']);

export function reservationStartAt(
  r: Pick<Reservation, 'reservationDate' | 'reservationTime'>,
): Date | null {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(r.reservationDate) ? r.reservationDate : null;
  const time = /^\d{2}:\d{2}/.test(r.reservationTime) ? r.reservationTime.slice(0, 5) : null;
  if (!date || !time) return null;
  const start = new Date(`${date}T${time}:00`);
  return Number.isNaN(start.getTime()) ? null : start;
}

export function reservationEndAt(
  r: Pick<Reservation, 'reservationDate' | 'reservationTime' | 'durationMinutes'>,
): Date | null {
  const start = reservationStartAt(r);
  if (!start) return null;
  const mins =
    Number.isFinite(r.durationMinutes) && r.durationMinutes > 0 ? r.durationMinutes : 0;
  return new Date(start.getTime() + mins * 60_000);
}

/** True si el turno (inicio + duración) ya terminó. */
export function isReservationPast(
  r: Pick<Reservation, 'reservationDate' | 'reservationTime' | 'durationMinutes'>,
  now = new Date(),
): boolean {
  const end = reservationEndAt(r);
  return end != null && end.getTime() <= now.getTime();
}

/** Se puede confirmar / sentar / marcar no-show solo si el turno no venció. */
export function isReservationActionable(r: Reservation, now = new Date()): boolean {
  return ACTIONABLE_STATUSES.has(r.status) && !isReservationPast(r, now);
}

/** Próximas accionables primero (asc por horario); el resto después (más reciente arriba). */
export function sortReservationsByUpcoming(
  list: Reservation[],
  now = new Date(),
): Reservation[] {
  const upcoming: Reservation[] = [];
  const rest: Reservation[] = [];
  for (const r of list) {
    (isReservationActionable(r, now) ? upcoming : rest).push(r);
  }
  const startOf = (r: Reservation) => reservationStartAt(r)?.getTime() ?? 0;
  upcoming.sort((a, b) => startOf(a) - startOf(b));
  rest.sort((a, b) => startOf(b) - startOf(a));
  return [...upcoming, ...rest];
}

/**
 * Split por tiempo del turno: próximas (turno no vencido, asc por horario)
 * y pasadas (turno vencido, más reciente arriba). Sin importar el estado.
 */
export function splitReservationsByTime(
  list: Reservation[],
  now = new Date(),
): { upcoming: Reservation[]; past: Reservation[] } {
  const upcoming: Reservation[] = [];
  const past: Reservation[] = [];
  for (const r of list) {
    (isReservationPast(r, now) ? past : upcoming).push(r);
  }
  const startOf = (r: Reservation) => reservationStartAt(r)?.getTime() ?? 0;
  upcoming.sort((a, b) => startOf(a) - startOf(b));
  past.sort((a, b) => startOf(b) - startOf(a));
  return { upcoming, past };
}

export type ReservationDayGroup = { day: string; items: Reservation[] };

/**
 * Agrupa por día (YYYY-MM-DD) conservando el orden de entrada.
 * Fecha inválida → grupo 'sin-fecha' al final del tramo donde aparece.
 */
export function groupReservationsByDay(list: Reservation[]): ReservationDayGroup[] {
  const groups: ReservationDayGroup[] = [];
  const indexByDay = new Map<string, number>();
  for (const r of list) {
    const day = /^\d{4}-\d{2}-\d{2}$/.test(r.reservationDate) ? r.reservationDate : 'sin-fecha';
    const idx = indexByDay.get(day);
    if (idx == null) {
      indexByDay.set(day, groups.length);
      groups.push({ day, items: [r] });
    } else {
      groups[idx].items.push(r);
    }
  }
  return groups;
}
